// Honest, transparent trend extrapolation for price series. Linear least-squares
// on the last N points projected forward, with a volatility band from the daily
// returns' standard deviation. NO econometric modelling — this is a visual
// "if the recent trend continued" guide, labelled as reference-only in the UI.

export interface TrendPoint {
  time: number; // unix seconds
  value: number;
}

export interface TrendResult {
  projected: TrendPoint[];
  upper: TrendPoint[];
  lower: TrendPoint[];
  slopePctPerWeek: number;
}

const DAY_SECONDS = 86400;

// Shared descriptive-stats helpers. Kept here (not a new stats.ts) so metrics.ts
// and linearTrend reuse one implementation instead of duplicating mean/std.
export function mean(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

// Population standard deviation. Returns 0 for <2 points.
export function std(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  const varSum = xs.reduce((s, x) => s + (x - m) ** 2, 0) / xs.length;
  return Math.sqrt(varSum);
}

// Fit y = a + b*x over the last `window` points; project `horizonDays` days ahead.
// Returns null when there aren't enough points to fit a meaningful trend.
export function linearTrend(
  points: TrendPoint[],
  horizonDays = 14,
  window = 30,
): TrendResult | null {
  const clean = points.filter((p) => Number.isFinite(p.value)).slice(-window);
  if (clean.length < 20) return null;

  const n = clean.length;
  // Use index as x (evenly spaced enough for a daily series) for numerical stability.
  const xs = clean.map((_, i) => i);
  const ys = clean.map((p) => p.value);
  const meanX = xs.reduce((s, x) => s + x, 0) / n;
  const meanY = ys.reduce((s, y) => s + y, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i] - meanX) * (ys[i] - meanY);
    den += (xs[i] - meanX) ** 2;
  }
  if (den === 0) return null;
  const b = num / den; // slope per index-step (≈ per day)
  const a = meanY - b * meanX;

  // Volatility: std of daily returns over the window.
  const returns: number[] = [];
  for (let i = 1; i < n; i++) {
    if (ys[i - 1] !== 0) returns.push((ys[i] - ys[i - 1]) / ys[i - 1]);
  }
  const stdR = std(returns);

  const lastTime = clean[n - 1].time;
  const lastValue = ys[n - 1];
  const projected: TrendPoint[] = [];
  const upper: TrendPoint[] = [];
  const lower: TrendPoint[] = [];
  for (let d = 1; d <= horizonDays; d++) {
    const time = lastTime + d * DAY_SECONDS;
    const value = a + b * (n - 1 + d);
    // Band widens with the square root of the horizon (random-walk-ish).
    const bandWidth = lastValue * 1.5 * stdR * Math.sqrt(d);
    projected.push({ time, value });
    upper.push({ time, value: value + bandWidth });
    lower.push({ time, value: value - bandWidth });
  }

  // Slope as % of last value per week (7 index-steps).
  const slopePctPerWeek = lastValue !== 0 ? ((b * 7) / lastValue) * 100 : 0;

  return { projected, upper, lower, slopePctPerWeek };
}
